import { useCallback, useEffect, useRef, useState } from 'react'

// Walks up from the element to the nearest scrollable ancestor so the observer
// measures visibility against the real scroll container (the page content div),
// not the viewport — the container clips rows via `overflow-y: auto`, which a
// viewport root would ignore.
function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null
  while (node) {
    const overflowY = getComputedStyle(node).overflowY
    if (overflowY === 'auto' || overflowY === 'scroll') return node
    node = node.parentElement
  }
  return null
}

// Observes a row and reports true when it is fully outside the scroll
// container's viewport (scrolled off the top or the bottom). Returns a callback
// ref so the observer re-binds whenever the target element mounts or unmounts —
// the row is conditionally rendered, so a plain ref would attach nothing on the
// first render and never re-run. Drives the bottom floating card: hidden while
// the real row is on screen, shown the moment it leaves in either direction.
export function useRowOutOfView(): [
  (node: HTMLElement | null) => void,
  boolean,
] {
  const [outOfView, setOutOfView] = useState(false)
  const observerRef = useRef<IntersectionObserver | null>(null)

  const setTarget = useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect()
    observerRef.current = null

    if (!node || typeof IntersectionObserver === 'undefined') {
      setOutOfView(false)
      return
    }

    const root = findScrollParent(node)
    const observer = new IntersectionObserver(
      ([entry]) => setOutOfView(!entry.isIntersecting),
      { root, threshold: 0 }
    )
    observer.observe(node)
    observerRef.current = observer
  }, [])

  useEffect(() => () => observerRef.current?.disconnect(), [])

  return [setTarget, outOfView]
}
